<template>
  <div style="display: flex; align-items: center; height: 100%; width: 100%;">

    <!-- Combined: Work required (unfilled) + Work completed (filled) -->
    <div v-if="(isCareGapTask && careGapWorkStatus) || sortedWorkEntries.length > 0" class="flex items-center gap-1 p-1">
      <div :class="stacked ? 'flex items-center gap-2' : 'flex items-center gap-1 flex-wrap'">
        <!-- Work required (not yet completed) — shown as hollow outlines with icons -->
        <div v-if="!stacked && isCareGapTask && careGapWorkStatus && incompleteWork.length" class="contents">
          <div
            v-for="req in incompleteWork"
            :key="'req-' + req.workTypeName"
            class="w-6 h-6 rounded-full flex items-center justify-center text-[10px] cursor-pointer border-2"
            :class="getReqBorderColor(req.workTypeName)"
            :title="`${req.displayName}: ${req.completedCount}/${req.requiredCount}`"
          >
            <font-awesome-icon :icon="getReqIcon(req.workTypeName)" />
          </div>
        </div>

        <div
          v-if="hiddenWorkCount > 0"
          class="w-6 h-6 rounded-full flex items-center justify-center bg-gray-300 text-gray-700 text-[10px] font-semibold shrink-0"
          :title="`${hiddenWorkCount} earlier work item${hiddenWorkCount === 1 ? '' : 's'}`"
        >+{{ hiddenWorkCount }}</div>

        <!-- Work completed — filled dots -->
        <div v-if="sortedWorkEntries.length" :class="stacked ? 'flex items-center -space-x-2.5' : 'contents'">
        <div
          v-for="(work, index) in visibleWorkEntries"
          :key="`${work.id}-${index}`"
          class="relative group"
          :class="stacked ? 'hover:z-10' : ''"
        >
          <div
            :ref="`work-dot-${index}`"
            class="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold cursor-pointer transition-transform hover:scale-125"
            :class="[getWorkTypeColor(work.work_type_name), stacked ? 'ring-2 ring-white' : '']"
            @mouseenter="handleDotHover(index, $event)"
            @mouseleave="hoveredWorkIndex = null"
          >
            <font-awesome-icon v-if="getWorkIcon(work)" :icon="getWorkIcon(work)" />
            <span v-else>✓</span>
          </div>

          <!-- Custom Tooltip using Teleport to escape cell boundaries -->
          <Teleport to="body">
            <div
              v-if="hoveredWorkIndex === index"
              class="fixed z-[9999] pointer-events-none"
              :style="{
                top: tooltipPosition.top + 'px',
                left: tooltipPosition.left + 'px',
                transform: 'translate(-50%, -100%)',
                marginTop: '-8px'
              }"
            >
              <div class="bg-gray-900 text-white text-xs rounded-lg shadow-xl p-3 whitespace-pre-line">
                {{ getWorkTooltip(work) }}
                <div class="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
              </div>
            </div>
          </Teleport>
        </div>
        </div>
      </div>
    </div>

    <!-- Fallback: Suggested codes + QA status -->
    <div v-if="!isCareGapTask && sortedWorkEntries.length === 0">
      <div v-if="suggestedCodes.length" class="suggested-codes">
        <div
          v-for="code in suggestedCodes"
          :key="code.code || code.id"
          class="code-pill"
          :class="code.statusClass"
          :title="code.tooltip"
        >
          <span class="code-text">{{ code.code || '—' }}</span>
          <span class="icon" aria-hidden="true">{{ code.icon }}</span>
        </div>
      </div>
      <span v-else class="text-gray-400 text-xs" style="line-height: var(--ag-row-height, 100px);">No work or suggested codes</span>
    </div>
  </div>
</template>

<script>
const MAX_STACKED_WORK = 5;
const COMPLETED_STATUSES = ['completed', 'complete', 'done', 'approved', 'accepted'];

export default {
  name: 'CC_Work_Summary_Column',
  props: {
    params: { type: Object, required: true }
  },
  data() {
    return {
      hoveredWorkIndex: null,
      tooltipPosition: { top: 0, left: 0 }
    };
  },
  computed: {
    stacked() {
      return !!this.params?.stacked;
    },
    rowData() {
      return this.params?.data || {};
    },
    workCompleted() {
      return this.rowData.work_completed || [];
    },
    sortedWorkEntries() {
      // Sort work entries by created_at chronologically (oldest to newest)
      return [...this.workCompleted].sort((a, b) =>
        new Date(a.created_at) - new Date(b.created_at)
      );
    },
    visibleWorkEntries() {
      if (!this.stacked) return this.sortedWorkEntries;
      return this.sortedWorkEntries.slice(-MAX_STACKED_WORK);
    },
    hiddenWorkCount() {
      return this.sortedWorkEntries.length - this.visibleWorkEntries.length;
    },
    rawSuggested() {
      const md = this.rowData.metadata || {};
      return md.codes_suggested || md.codessuggested || [];
    },
    currentTaskOrder() {
      // Fallback to 1 if not provided
      return Number(this.rowData.task_order) || 1;
    },
    suggestedCodes() {
      return this.rawSuggested.map(c => {
        const qaArray = Array.isArray(c.qa) ? c.qa : [];
        const requiredLevel = this.currentTaskOrder;
        const needsQA = requiredLevel > 1;

        const matchingQA = qaArray.filter(q => Number(q.taskType) === requiredLevel);
        const isComplete = matchingQA.some(q =>
          COMPLETED_STATUSES.includes(String(q.qa_status || '').toLowerCase())
        );

        let status = 'none';
        if (needsQA && isComplete) status = 'complete';
        else if (needsQA && !isComplete) status = 'pending';

        const statusMeta = {
          complete: { icon: '✅', cls: 'status-complete', label: 'QA complete' },
          pending: { icon: '⚠️', cls: 'status-pending', label: 'QA pending' },
            // eslint-disable-next-line
          none: { icon: '•', cls: 'status-none', label: 'No QA required' }
        }[status];

        const tooltipLines = [
          `Code: ${c.code || 'N/A'}`,
          `Required QA Level: ${needsQA ? requiredLevel : 'None'}`,
          `Status: ${statusMeta.label}`,
          matchingQA.length ? `Attempts: ${matchingQA.length}` : null
        ].filter(Boolean);

        return {
          ...c,
          icon: statusMeta.icon,
          statusClass: statusMeta.cls,
          tooltip: tooltipLines.join('\n')
        };
      });
    },

    // Care Gap specific computed properties
    isCareGapTask() {
      return this.rowData.task_type === 16;
    },

    // Get opportunities from the task (each opportunity has work_status from the serializer)
    opportunities() {
      return this.rowData.opportunities || [];
    },

    // Aggregate work status across all opportunities for this task
    // Uses the work_status computed by OpportunitySerializer which checks OpportunityWorkTemplates
    careGapWorkStatus() {
      if (!this.isCareGapTask || this.opportunities.length === 0) return null;

      // Collect all work_status items from all opportunities
      const workStatusByType = new Map();

      this.opportunities.forEach(opp => {
        const workStatus = opp.work_status || [];
        workStatus.forEach(status => {
          const key = status.work_type_name;
          if (!workStatusByType.has(key)) {
            workStatusByType.set(key, {
              workTypeName: status.work_type_name,
              displayName: status.work_type_abbreviated || status.work_type_display_name,
              requiredCount: 0,
              completedCount: 0,
              perOpportunity: status.per_opportunity,
              isComplete: true // Will be set to false if any opportunity is incomplete
            });
          }

          const aggregated = workStatusByType.get(key);

          if (status.per_opportunity) {
            // Per-opportunity work: sum up required and completed across opportunities
            aggregated.requiredCount += status.required_count;
            aggregated.completedCount += status.completed_count;
            if (!status.is_complete) {
              aggregated.isComplete = false;
            }
          } else {
            // Per-task work: use the first opportunity's status (they should all be the same)
            aggregated.requiredCount = status.required_count;
            aggregated.completedCount = status.completed_count;
            aggregated.isComplete = status.is_complete;
          }
        });
      });

      // Convert map to array
      return Array.from(workStatusByType.values());
    },

    incompleteWork() {
      if (!this.careGapWorkStatus) return [];
      return this.careGapWorkStatus.filter(req => !req.isComplete);
    },

    allCareGapWorkComplete() {
      if (!this.careGapWorkStatus) return false;
      return this.careGapWorkStatus.every(req => req.isComplete);
    }
  },
  methods: {
    getWorkIcon(work) {
      try {
        if (!work.work_type_icon) return null;

        // Parse the string representation of the array
        // Example: "['fas','file-invoice']" -> ['fas', 'file-invoice']
        const iconString = work.work_type_icon.replace(/'/g, '"');
        const iconArray = JSON.parse(iconString);

        if (Array.isArray(iconArray) && iconArray.length === 2) {
          return iconArray;
        }
        return null;
      } catch (error) {
        console.warn('Failed to parse work_type_icon:', work.work_type_icon, error);
        return null;
      }
    },

    getReqIcon(workTypeName) {
      const iconMap = {
        'medical_record_review_care_gaps': ['fas', 'file-medical'],
        'care_gap_patient_call': ['fas', 'phone'],
        'care_gap_send_list_to_practice': ['fas', 'list'],
        'care_gap_action_outcome': ['fas', 'check'],
        'med_adherence_pharmacy_call': ['fas', 'prescription-bottle'],
        'med_adherence_provider_call': ['fas', 'user-doctor'],
        'med_adherence_patient_call': ['fas', 'phone'],
        'med_adherence_outcome': ['fas', 'check'],
      };
      return iconMap[workTypeName] || ['fas', 'circle'];
    },

    getReqBorderColor(workTypeName) {
      const colorMap = {
        'care_gap_patient_call': 'border-primary-400 text-primary-400',
        'medical_record_review_care_gaps': 'border-purple-400 text-purple-400',
        'care_gap_send_list_to_practice': 'border-yellow-500 text-yellow-500',
        'care_gap_action_outcome': 'border-gray-400 text-gray-400',
        'med_adherence_pharmacy_call': 'border-blue-400 text-blue-400',
        'med_adherence_provider_call': 'border-teal-400 text-teal-400',
        'med_adherence_patient_call': 'border-primary-400 text-primary-400',
        'med_adherence_outcome': 'border-gray-400 text-gray-400',
      };
      return colorMap[workTypeName] || 'border-gray-400 text-gray-400';
    },

    handleDotHover(index, event) {
      this.hoveredWorkIndex = index;
      const rect = event.target.getBoundingClientRect();
      this.tooltipPosition = {
        top: rect.top,
        left: rect.left + rect.width / 2
      };
    },

    getWorkTypeColor(workTypeName) {
      const colorMap = {
        'care_gap_patient_call': 'bg-primary-500',
        'medical_record_review_care_gaps': 'bg-purple-500',
        'care_gap_send_list_to_practice': 'bg-yellow-500',
        'default': 'bg-gray-500'
      };
      return colorMap[workTypeName] || colorMap.default;
    },

    getWorkTooltip(work) {
      const lines = [
        `${work.work_type_display_name || work.work_type_abbreviated_name || 'Work'}`,
        `---`,
        `Date: ${this.formatDateTime(work.created_at)}`,
        `By: ${work.appuser_name || 'Unknown'}`,
      ];

      // Check for call_entries - if exists, display all entries
      if (work.metadata?.call_entries && Array.isArray(work.metadata.call_entries) && work.metadata.call_entries.length > 0) {
        lines.push(''); // Empty line for spacing
        lines.push(`Call Entries (${work.metadata.call_entries.length} total):`);

        work.metadata.call_entries.forEach((entry, index) => {
          lines.push(`  Entry #${index + 1}:`);

          // Display available fields from call entry
          if (entry.call_outcome) {
            lines.push(`    Outcome: ${entry.call_outcome}`);
          }
          if (entry.medication_category) {
            lines.push(`    Category: ${entry.medication_category}`);
          }
          if (entry.call_note) {
            lines.push(`    Note: ${entry.call_note}`);
          }
          if (entry.name) {
            lines.push(`    Name: ${entry.name}`);
          }
          if (entry.phone) {
            lines.push(`    Phone: ${entry.phone}`);
          }
          if (entry.status) {
            lines.push(`    Status: ${entry.status}`);
          }

          // Show all entry keys for debugging
          const entryKeys = Object.keys(entry);
          if (entryKeys.length > 0) {
            lines.push(`    [Fields: ${entryKeys.join(', ')}]`);
          }

          // Add spacing between entries
          if (index < work.metadata.call_entries.length - 1) {
            lines.push('');
          }
        });
      } else {
        // No call_entries, just show simple outcome
        if (work.metadata?.outcome) {
          lines.push(`Outcome: ${work.metadata.outcome}`);
        }
        if (work.metadata?.status) {
          lines.push(`Status: ${work.metadata.status}`);
        }
        if (work.metadata?.result) {
          lines.push(`Result: ${work.metadata.result}`);
        }
        if (work.metadata?.notes) {
          lines.push(`Notes: ${work.metadata.notes}`);
        }
      }

      return lines.join('\n');
    },

    formatDateTime(dateStr) {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      if (isNaN(date)) return dateStr;

      // Uses browser's local timezone automatically
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    }
  }
};
</script>

<style scoped>

</style>
